import { Component, Inject, OnInit } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material';
import { API_SERRI_LIST_TEMPLATES, API_SERRI_LIST_ACCOUNTS, API_WHATSAPP_LIST_TEMPLATES, API_WHATSAPP_LIST_WABAs, PROJECT_API_KEY } from 'src/@core/services/util/api.endpoints';
import { environment } from 'src/environments/environment';

export interface WaTemplateLibraryDialogData {
  entityId: string;
  projectId?: string;
  whatsappPhoneNumber?: string;
  providerType: string;
  selectedName?: string;
  getTemplateValue: (template: any) => string;
  templates?: any[];
}

@Component({
  selector: 'hour4u-admin-wa-template-library-dialog',
  templateUrl: './wa-template-library-dialog.component.html',
  styleUrls: ['./wa-template-library-dialog.component.scss']
})
export class WaTemplateLibraryDialogComponent implements OnInit {
  searchQuery = '';
  templates: any[] = [];
  isLoading = false;
  loadError = '';

  constructor(
    private dialogRef: MatDialogRef<WaTemplateLibraryDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: WaTemplateLibraryDialogData,
    private http: HttpClient,
  ) { }

  ngOnInit(): void {
    this.templates = (this.data && this.data.templates) ? [...this.data.templates] : [];
    this.loadTemplates();
  }

  get filteredTemplates(): any[] {
    const list = this.templates || [];
    const q = (this.searchQuery || '').trim().toLowerCase();
    if (!q) {
      return list;
    }
    return list.filter((t) => {
      const name = this.getDisplayName(t).toLowerCase();
      const category = this.getCategory(t).toLowerCase();
      const body = this.getPreviewBody(t).toLowerCase();
      return name.indexOf(q) !== -1 || category.indexOf(q) !== -1 || body.indexOf(q) !== -1;
    });
  }

  loadTemplates(): void {
    const provider = ((this.data && this.data.providerType) || '').toLowerCase();

    if (provider === 'meta_api') {
      this.loadMetaTemplates();
      return;
    }
    if (provider === 'interakt') {
      this.loadInteraktTemplates();
      return;
    }

    // Default: Serri flow (same as WhatsApp > Templates tab)
    this.loadSerriTemplatesWithPhoneResolution();
  }

  private loadSerriTemplatesWithPhoneResolution(): void {
    if (!this.data || !this.data.entityId) {
      this.loadError = 'Entity ID is missing.';
      return;
    }

    const phone = (this.data.whatsappPhoneNumber || '').trim();
    if (phone) {
      this.fetchSerriTemplates(phone);
      return;
    }

    this.isLoading = true;
    this.loadError = '';

    const params = new URLSearchParams({ entity_id: this.data.entityId });
    this.http.get(`${API_SERRI_LIST_ACCOUNTS}?${params.toString()}`, { headers: this.authHeaders() }).subscribe({
      next: (response: any) => {
        const resolvedPhone = this.extractSerriPhoneFromAccountsResponse(response);
        if (!resolvedPhone) {
          this.isLoading = false;
          this.loadError = 'No Serri WhatsApp number found.';
          this.templates = [];
          return;
        }
        this.data.whatsappPhoneNumber = resolvedPhone;
        this.fetchSerriTemplates(resolvedPhone);
      },
      error: () => {
        this.isLoading = false;
        this.loadError = 'Failed to load Serri WhatsApp accounts.';
        this.templates = [];
      }
    });
  }

  private extractSerriPhoneFromAccountsResponse(response: any): string {
    let accounts: any[] = [];
    if (response && response.success && response.data && Array.isArray(response.data)) {
      accounts = response.data;
    } else if (Array.isArray(response)) {
      accounts = response;
    }
    if (!accounts.length) {
      return '';
    }
    const first = accounts[0];
    return String(first.whatsapp_phone_number || first.phone_number || '').trim();
  }

  private fetchSerriTemplates(whatsappPhoneNumber: string): void {
    if (!this.data || !this.data.entityId || !whatsappPhoneNumber) {
      return;
    }

    this.isLoading = true;
    this.loadError = '';

    const params = new URLSearchParams({
      entity_id: this.data.entityId,
      whatsapp_phone_number: whatsappPhoneNumber,
      page: '1',
      limit: '100'
    });

    this.http.get(`${API_SERRI_LIST_TEMPLATES}?${params.toString()}`, { headers: this.authHeaders() }).subscribe({
      next: (response: any) => {
        this.isLoading = false;
        let raw: any[] = [];
        if (response && response.success && response.data && response.data.data && Array.isArray(response.data.data)) {
          raw = response.data.data;
        } else if (response && response.success && response.data && Array.isArray(response.data)) {
          raw = response.data;
        } else if (Array.isArray(response)) {
          raw = response;
        }

        this.templates = (raw || []).map((t: any) => ({
          ...t,
          template_id: t.id || t.template_id,
          name: t.name || t.label || 'Template'
        }));
      },
      error: () => {
        this.isLoading = false;
        this.loadError = 'Failed to load Serri templates.';
        this.templates = [];
      }
    });
  }

  private loadSerriTemplates(): void {
    this.loadSerriTemplatesWithPhoneResolution();
  }

  private authHeaders(): HttpHeaders {
    const authToken = localStorage.getItem('jwtToken');
    return new HttpHeaders({
      'Content-Type': 'application/json; charset=utf-8',
      'Authorization': `Bearer ${authToken}`,
      'x-api-key': PROJECT_API_KEY,
      'x-suppress-error': 'true'
    });
  }

  private loadMetaTemplates(): void {
    if (!this.data.entityId) {
      return;
    }

    this.isLoading = true;
    this.loadError = '';

    const params = new URLSearchParams({ entity_id: this.data.entityId });
    this.http.get(`${API_WHATSAPP_LIST_WABAs}?${params.toString()}`, { headers: this.authHeaders() }).subscribe({
      next: (wabaResponse: any) => {
        if (!wabaResponse || !wabaResponse.success || !wabaResponse.data || !wabaResponse.data.length) {
          this.isLoading = false;
          this.templates = [];
          return;
        }

        const wabaId = wabaResponse.data[0].waba_id;
        const templateParams = new URLSearchParams({
          entity_id: this.data.entityId,
          waba_id: wabaId,
          limit: '100'
        });

        this.http.get(`${API_WHATSAPP_LIST_TEMPLATES}?${templateParams.toString()}`, { headers: this.authHeaders() }).subscribe({
          next: (templateResponse: any) => {
            this.isLoading = false;
            if (templateResponse && templateResponse.success && Array.isArray(templateResponse.data)) {
              this.templates = templateResponse.data.filter((t: any) => t.status === 'APPROVED');
            } else {
              this.templates = [];
            }
          },
          error: () => {
            this.isLoading = false;
            this.loadError = 'Failed to load Meta templates.';
            this.templates = [];
          }
        });
      },
      error: () => {
        this.isLoading = false;
        this.loadError = 'Failed to load Meta templates.';
        this.templates = [];
      }
    });
  }

  private loadInteraktTemplates(): void {
    const apiKey = environment.interaktApiKey;
    if (!apiKey) {
      this.loadError = 'Interakt API key not configured.';
      return;
    }

    this.isLoading = true;
    this.loadError = '';

    fetch('https://api.interakt.ai/v1/public/track/organization/templates?offset=0&autosubmitted_for=all&approval_status=APPROVED&language=all', {
      method: 'GET',
      headers: {
        'Authorization': `Basic ${apiKey}`,
        'Content-Type': 'application/json'
      }
    })
      .then(async (response) => {
        this.isLoading = false;
        if (!response.ok) {
          this.loadError = 'Failed to load Interakt templates.';
          this.templates = [];
          return;
        }
        const responseData = await response.json();
        let raw: any[] = [];
        if (Array.isArray(responseData)) {
          raw = responseData;
        } else if (responseData && Array.isArray(responseData.data)) {
          raw = responseData.data;
        } else if (responseData && responseData.results && Array.isArray(responseData.results)) {
          raw = responseData.results;
        }
        this.templates = raw || [];
      })
      .catch(() => {
        this.isLoading = false;
        this.loadError = 'Failed to load Interakt templates.';
        this.templates = [];
      });
  }

  getDisplayName(template: any): string {
    return template.name || template.template_name || template.label || 'Template';
  }

  getCategory(template: any): string {
    const cat = template.category || template.template_category || 'UTILITY';
    if (typeof cat === 'string') {
      return cat.charAt(0) + cat.slice(1).toLowerCase();
    }
    return 'Utility';
  }

  getPreviewBody(template: any): string {
    if (!template) {
      return '';
    }
    if (typeof template.text === 'string' && template.text.trim()) {
      return template.text.trim();
    }
    if (typeof template.sample_text === 'string' && template.sample_text.trim()) {
      return template.sample_text.trim();
    }
    if (typeof template.body === 'string' && template.body.trim()) {
      return template.body.trim();
    }
    if (Array.isArray(template.components)) {
      const body = template.components.find((c: any) => c.type === 'BODY' || c.type === 'body');
      if (body && body.text) {
        return body.text;
      }
    }
    return '';
  }

  isSelected(template: any): boolean {
    if (!this.data || !this.data.selectedName || !this.data.getTemplateValue) {
      return false;
    }
    return this.data.getTemplateValue(template) === String(this.data.selectedName);
  }

  getCategoryClass(template: any): string {
    const cat = this.getCategory(template).toLowerCase();
    if (cat.indexOf('re-engag') !== -1 || cat.indexOf('reengag') !== -1 || cat.indexOf('marketing') !== -1) {
      return 'wa-tpl-item__cat--reengage';
    }
    if (cat.indexOf('document') !== -1 || cat.indexOf('utility') !== -1) {
      return 'wa-tpl-item__cat--documents';
    }
    if (cat.indexOf('offer') !== -1) {
      return 'wa-tpl-item__cat--offer';
    }
    if (cat.indexOf('welcome') !== -1 || cat.indexOf('auth') !== -1) {
      return 'wa-tpl-item__cat--welcome';
    }
    return 'wa-tpl-item__cat--default';
  }

  selectTemplate(template: any): void {
    this.dialogRef.close(template);
  }

  onCreateNew(): void {
    this.dialogRef.close({ createNew: true });
  }

  close(): void {
    this.dialogRef.close();
  }
}
